// Copyright 2026 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import * as Setting from "../Setting";
import * as Conf from "../Conf";

function buildUrl(path, query = {}) {
  const url = new URL(`${Setting.getFullServerUrl()}${path}`);

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    url.searchParams.set(key, value);
  });

  return url.toString();
}

function getHeaders(extraHeaders = {}) {
  return {
    "Accept-Language": Setting.getAcceptLanguage(),
    ...extraHeaders,
  };
}

async function requestJSON(path, options = {}, query = {}) {
  const response = await fetch(buildUrl(path, query), {
    credentials: "include",
    ...options,
    headers: getHeaders(options.headers),
  });

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    const error = new Error(data?.msg || data?.message || text || `Request failed with status ${response.status}`);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
}

export function getChats({cursor = "", limit = Conf.AiHistoryPageSize, service = Conf.AiGatewayService} = {}) {
  return requestJSON("/api/kmwork-ai/chats", {method: "GET"}, {cursor, limit, service});
}

export function getChat(chatId) {
  return requestJSON(`/api/kmwork-ai/chats/${encodeURIComponent(chatId)}`, {method: "GET"});
}

export function getChatMessages(chatId) {
  return requestJSON(`/api/kmwork-ai/chats/${encodeURIComponent(chatId)}/messages`, {method: "GET"});
}

export function deleteChat(chatId) {
  return requestJSON(`/api/kmwork-ai/chats/${encodeURIComponent(chatId)}`, {method: "DELETE"});
}

export function stopChat(chatId) {
  return requestJSON(`/api/kmwork-ai/chats/${encodeURIComponent(chatId)}/stop`, {method: "POST"});
}

export function getModels() {
  return requestJSON("/api/kmwork-ai/models", {method: "GET"});
}

export function getProviders({service = Conf.AiGatewayService} = {}) {
  return requestJSON("/api/kmwork-ai/providers", {method: "GET"}, {service});
}

export function getAgents({service = Conf.AiGatewayService} = {}) {
  return requestJSON("/api/kmwork-ai/agents", {method: "GET"}, {service});
}

function getDraftTargetSummary(draft) {
  const target = draft?.target || {};
  const fields = draft?.fields || {};

  if (draft?.entity === "application") {
    return [target.organization || fields.organization || target.owner || fields.owner || "", target.name || fields.name || ""].filter(Boolean).join("/");
  }

  return [target.owner || fields.owner || "", target.name || fields.name || ""].filter(Boolean).join("/");
}

export function recordDraftEvent(event, draft, extra = {}) {
  return requestJSON("/api/kmwork-ai/draft-records", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      event,
      entity: draft?.entity || "",
      action: draft?.action || "edit",
      targetSummary: extra?.targetSummary || getDraftTargetSummary(draft),
      draft,
      extra,
    }),
  });
}

export function validateDraft(draft) {
  return requestJSON("/api/kmwork-ai/draft-validations", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({draft}),
  });
}
